import React, { useEffect, useState } from 'react';
import OrderKanban from './OrderKanban';
import { PurchaseOrder, OrderStatus } from '@/types';
import { apiService } from '@/services/apiService';
import { useModal } from './ModalProvider';
import { Paperclip, Download } from 'lucide-react';

const OrderBoard: React.FC = () => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const { showAlert } = useModal();

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiService.getOrders();
      setOrders(data);
    } catch (e) {
      console.error('Erro ao carregar ordens (kanban):', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
    try {
      await apiService.updateOrder(orderId, { status: newStatus });
      await load();
    } catch (e) {
      console.error(e);
      await showAlert('Erro ao atualizar status.');
    }
  };

  const openDetail = (order: PurchaseOrder) => {
    setSelectedOrder(order);
    setFile(null);
    setIsDetailOpen(true);
  };

  const handleSaveDetail = async (updated: Partial<PurchaseOrder>) => {
    if (!selectedOrder) return;
    try {
      let fileUrl = selectedOrder.fileUrl;
      if (file) {
        const fileExt = file.name.split('.').pop();
        const key = `orders/${selectedOrder.companyId || 'general'}/${globalThis.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).substr(2))}.${fileExt}`;
        const { uploadUrl } = await apiService.getPresignedUploadUrl(key, file.type);
        await apiService.uploadFileToS3(uploadUrl, file);
        fileUrl = key;
      }

      await apiService.updateOrder(selectedOrder.id, { ...selectedOrder, ...updated, fileUrl });
      await load();
      setIsDetailOpen(false);
      await showAlert('Alterações salvas com sucesso.');
    } catch (e) {
      console.error('Erro ao salvar detalhe:', e);
      await showAlert('Erro ao salvar alterações.');
    }
  };

  const handleDownload = async (order: PurchaseOrder) => {
    try {
      if (order.fileUrl) {
        const { downloadUrl } = await apiService.getPresignedDownloadUrl(order.fileUrl);
        window.open(downloadUrl, '_blank');
        return;
      }
      if (order.file && order.file.base64) {
        const link = document.createElement('a');
        link.href = `data:${order.file.type};base64,${order.file.base64}`;
        link.download = order.file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      await showAlert('Nenhum anexo disponível.');
    } catch (e) {
      console.error(e);
      await showAlert('Erro ao baixar anexo.');
    }
  };

  return (
    <div className="space-y-6 pb-8">
      <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark p-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">Quadro - Empenhos e O.S.</h2>
          <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-1">Visão Kanban sincronizada com as ordens do sistema.</p>
        </div>
        <div>
          <button onClick={load} className="px-3 py-2 bg-surface dark:bg-surface-dark border rounded">Atualizar</button>
        </div>
      </div>

      <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark overflow-x-auto">
        {loading ? (
          <div className="p-6 text-center text-ink-subtitle dark:text-ink-subtitle-dark">Carregando ordens...</div>
        ) : (
          <OrderKanban orders={orders} onCardDoubleClick={openDetail} onStatusChange={handleStatusChange} />
        )}
      </div>

      {/* Detail Modal (local) */}
      {isDetailOpen && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
              <h3 className="font-semibold text-ink-title dark:text-ink-title-dark">Detalhes - {selectedOrder.number}</h3>
              <button onClick={() => setIsDetailOpen(false)} className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark">Fechar</button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              <div>
                <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Título</label>
                <input className="w-full border p-2 rounded" defaultValue={selectedOrder.title} id="kb-title" />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Descrição / Comentários</label>
                <textarea defaultValue={selectedOrder.description || ''} id="kb-desc" className="w-full border p-2 rounded h-28" />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Arquivo (opcional)</label>
                <input type="file" accept=".pdf,.html,.htm,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                <div className="mt-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark flex items-center gap-2">
                  {selectedOrder.fileUrl || selectedOrder.file ? (
                    <>
                      <Paperclip className="w-4 h-4" />
                      <button onClick={() => handleDownload(selectedOrder)} className="underline">Download do Anexo</button>
                    </>
                  ) : (<span>Sem anexo</span>)}
                </div>
              </div>
            </div>
            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
              <button onClick={() => setIsDetailOpen(false)} className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg">Cancelar</button>
              <button onClick={async () => {
                const title = (document.getElementById('kb-title') as HTMLInputElement).value;
                const description = (document.getElementById('kb-desc') as HTMLTextAreaElement).value;
                await handleSaveDetail({ title, description });
              }} className="px-4 py-2 text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 rounded-lg">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderBoard;
